import { memo, useEffect } from "react";
import { useUser } from "@clerk/react";
import { useStore } from "../store/globalState";
import { useTranslation } from "react-i18next";
import {
  Button,
  CashSpecialIcon,
  GradientText,
  Layout,
  SupportIcon,
  Title,
} from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { FlowHeroMark } from "../components/FlowHeroMark";
import { authPagePath } from "../auth/resume";
import { APP_ROUTES, TITLE_COLOR } from "../constants";
import { formatLabeledAmount } from "../utils";
import { usablePersonName } from "../utils/homeGreeting";
import { useClaimLink } from "../hooks/useClaimLink";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useSessionStore } from "../store/useSessionStore";
import { usePayoutClaimPricing } from "../hooks/usePayoutClaimPricing";
import { useClaimableGuard } from "../hooks/useClaimableGuard";
import { getEvents, safeCapture } from "../analytics";
import { RequestStatus } from "../utils/commons";
import { ConsumerSessionMenu } from "../components/ConsumerSessionMenu";
import { getClerkConsumerPublishableKey } from "../auth/clerkPublishableKey";
import { useSupportSheet } from "../hooks/useSupportSheet";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { blurActiveTextInput } from "../utils/blurActiveTextInput";
import { Navbar } from "../components/Navbar";

/**
 * Entry screen for payout-claim: someone sent you money.
 * Layout matches fund-request (need-help header, session chrome if signed in,
 * left Title, white disc, amount).
 */
interface PayoutClaimEntryPageContentProps {
  isSignedIn?: boolean;
}

export const PayoutClaimEntryPage = memo(() => {
  if (!getClerkConsumerPublishableKey()) {
    return <PayoutClaimEntryPageContent />;
  }
  return <SignedInAwarePayoutClaimEntryPage />;
});

const SignedInAwarePayoutClaimEntryPage = memo(() => {
  const { isSignedIn } = useUser();
  return <PayoutClaimEntryPageContent isSignedIn={isSignedIn} />;
});

const PayoutClaimEntryPageContent = memo(({
  isSignedIn = false,
}: PayoutClaimEntryPageContentProps) => {
  useClaimLink();
  useClaimableGuard();
  const { t } = useTranslation();
  const { session } = useSessionStore();
  const { theme } = useStore();
  const { redirectTo } = useRouteWithToken();

  useEffect(() => {
    safeCapture(getEvents().CLAIM_ENTRY_VIEW);
  }, []);

  const colorConfig = theme.whiteView;
  const {
    background,
    backgroundBtn,
    textColorBtn,
    dropzoneColors,
    backgroundIcon,
  } = colorConfig;
  const sessionReady =
    session.status === RequestStatus.LOADED && Boolean(session.data);
  const config = session.data?.config;
  const senderName =
    usablePersonName(config?.senderName as string | undefined) ||
    t("payoutClaim.someone");

  const { sendAmount, sourceCurrency, destinationCurrency, economics, receiveRange } =
    usePayoutClaimPricing({ enableQuote: false });

  const previewReceive =
    receiveRange?.max ?? economics?.receiveAmount ?? null;
  const amountLine =
    previewReceive != null
      ? formatLabeledAmount(previewReceive, destinationCurrency)
      : formatLabeledAmount(sendAmount, sourceCurrency);

  const handleLater = () => {
    if (!sessionReady) return;
    safeCapture(getEvents().CLAIM_ENTRY_LATER_CLICK);
    redirectTo(APP_ROUTES.LATER_PAGE);
  };

  const handleDiscard = () => {
    if (!sessionReady) return;
    safeCapture(getEvents().CLAIM_ENTRY_DISCARD_CLICK);
    redirectTo(APP_ROUTES.DISCARD_PAGE);
  };

  const handleClaim = () => {
    if (!sessionReady) return;
    safeCapture(getEvents().CLAIM_ENTRY_CLAIM_CLICK);
    redirectTo(authPagePath({ kind: "transfer" }, { fromClaim: true }));
  };

  const { openSupport, supportSheet } = useSupportSheet();

  return (
    <>
      <AuraLayout colorConfig={colorConfig} className="scrollable need-help-header">
        <Layout.Header>
          {isSignedIn && (
            <ConsumerSessionMenu
              resume={{ kind: "transfer" }}
              fromClaim
              analyticsScreen="claim_entry"
            />
          )}
          <Navbar
            title={t("common.needHelp")}
            align="right"
            noBackButton
            onIconClick={() => openSupport()}
            icon={<SupportIcon size={35} colors={dropzoneColors} />}
          />
        </Layout.Header>
        <Layout.Content className="landing-section fund-request-section">
          <ScrollFadeIndicator color={colorConfig.bodyCopy} />
          <Title
            title={t("payoutClaim.sentYou", { name: senderName })}
            align="left"
            color={TITLE_COLOR}
          />
          <section className="fund-request-hero">
            <div className="fund-request-disc">
              <FlowHeroMark
                dropzoneColors={dropzoneColors}
                backgroundIcon={backgroundIcon}
              >
                <CashSpecialIcon size={60} colors={dropzoneColors} />
              </FlowHeroMark>
            </div>

            <section className="landing-container fund-request-amount">
              <GradientText
                as="label"
                textColor={TITLE_COLOR}
                className="fund-request-label"
              >
                {t("payoutClaim.theySentYou")}
              </GradientText>
              <GradientText as="h1" textColor={TITLE_COLOR}>
                {amountLine}
              </GradientText>
            </section>
          </section>
        </Layout.Content>

        <Layout.Footer>
          <section className="footer-section">
            <Button
              type="button"
              variant="cancel"
              size="small"
              color={TITLE_COLOR}
              className="fund-request-discard"
              disabled={!sessionReady}
              onClick={handleDiscard}
            >
              {t("payoutClaim.discard")}
            </Button>
            <section className="fund-request-actions fund-request-actions--stack">
              <Button
                type="button"
                size="large"
                variant="outline"
                background={backgroundBtn}
                color={TITLE_COLOR}
                style={{ backgroundColor: background }}
                disabled={!sessionReady}
                onClick={handleLater}
              >
                {t("payoutClaim.later")}
              </Button>
              <Button
                type="button"
                size="large"
                background={backgroundBtn}
                color={textColorBtn}
                disabled={!sessionReady}
                onPointerDown={blurActiveTextInput}
                onClick={handleClaim}
              >
                {t("payoutClaim.claim")}
              </Button>
            </section>
          </section>
        </Layout.Footer>
      </AuraLayout>
      {supportSheet}
    </>
  );
});
